import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, forkJoin, map, of, tap } from 'rxjs';
import { GetInvestigationPermissionsUseCase } from 'src/app/auth/application/use-cases/get-investigation-permissions/get-investigation-permissions.use-case';
import { HasInvestigationPermissionUseCase } from 'src/app/auth/application/use-cases/has-investigation-permission/has-investigation-permission.use-case';
import { InvestigationPermission } from 'src/app/auth/domain/enums/investigation-permission.enum';
import { GetMyManagedTeamsUseCase } from '@features/investigations';
import { NavBarProvider, NavItemSource } from '../providers';

export const INVESTIGATIONS_NAV_ITEMS: NavItemSource<InvestigationPermission>[] = [
  {
    id: 'investigation-dashboard',
    title: 'Dashboard',
    anchorHref: '/investigations/dashboard',
    requiredPermission: InvestigationPermission.CaseManagementViewOpen,
  },
  {
    id: 'investigation-intake-queue',
    title: 'Intake Queue',
    anchorHref: '/investigations/intake-queue',
    requiredPermission: InvestigationPermission.CaseManagementViewIntakeQueue,
  },
  {
    id: 'investigations-new-investigation',
    title: 'New Investigation',
    anchorHref: '/investigations/create',
    requiredPermission: InvestigationPermission.CaseManagementCreate,
  },
  {
    id: 'investigation-approvals',
    title: 'Approvals',
    anchorHref: '/investigations/approvals',
    requiredPermission: InvestigationPermission.CaseManagementApprove,
  },
  {
    id: 'investigation-team-workload',
    title: 'Team Workload',
    anchorHref: '/investigations/workload',
  },
  {
    id: 'investigation-my-watches',
    title: 'My Watches',
    anchorHref: '/investigations/my-watches',
    requiredPermission: InvestigationPermission.CaseManagementViewOpen,
  },
  {
    id: 'investigation-audit-log',
    title: 'Audit Log',
    anchorHref: '/investigations/audit-log',
    requiredPermission: InvestigationPermission.CaseManagementViewAuditLog,
  },
  {
    id: 'investigation-reports',
    title: 'Reports',
    anchorHref: '/investigations/reports',
    requiredPermission: InvestigationPermission.AnalyticsView,
  },
  {
    id: 'investigation-admin',
    title: 'Admin Settings',
    anchorHref: '/investigations/admin',
    requiredPermission: InvestigationPermission.CaseManagementAccessSettings,
  },
];

export const setInvestigationsNavigationResolver: ResolveFn<NavItemSource<InvestigationPermission>[]> = () => {
  const navBarProvider = inject(NavBarProvider);
  const getPermissionsUseCase = inject(GetInvestigationPermissionsUseCase);
  const hasPermissionUseCase = inject(HasInvestigationPermissionUseCase);
  const getManagedTeamsUseCase = inject(GetMyManagedTeamsUseCase);

  navBarProvider.setLoadingNavigation(true);

  return forkJoin({
    _: getPermissionsUseCase.execute(),
    teams: getManagedTeamsUseCase.execute().pipe(catchError(() => of([]))),
  }).pipe(
    map(({ teams }) => {
      const hasTeams = teams.length > 0;
      return INVESTIGATIONS_NAV_ITEMS.filter((source) => {
        if (source.id === 'investigation-team-workload') return hasTeams;
        if (source.id === 'investigations-new-investigation') {
          return hasPermissionUseCase.execute(InvestigationPermission.CaseManagementCreate) &&
            !hasPermissionUseCase.execute(InvestigationPermission.CaseManagementViewOpen);
        }
        if (!source.requiredPermission) return true;
        return hasPermissionUseCase.execute(source.requiredPermission);
      });
    }),
    tap((filtered) => {
      navBarProvider.updateNavigation(filtered);
      navBarProvider.setLoadingNavigation(false);
    }),
    catchError(() => {
      navBarProvider.updateNavigation([]);
      navBarProvider.setLoadingNavigation(false);
      return of([]);
    }),
  );
};
